import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import type { AxiosError } from 'axios';
import { ArrowLeft, Mail, Pencil, Phone, Trash2, UserX } from 'lucide-react';
import clsx from 'clsx';

import Avatar from '@/components/Avatar';
import Sheet from '@/components/Sheet';
import StatusPill from '@/components/StatusPill';
import {
  AccrualsList,
  MemberStatsGrid,
  MonthsTable,
  PayoutsList,
  establishmentsCount,
  type AccrualItem,
  type MemberStatsData,
  type MonthItem,
  type PayoutItem,
} from '@/components/MemberStats';
import { apiDelete, apiErrorMessage, apiGet, apiPatch } from '@/lib/api';
import { readBackState, type BackState } from '@/lib/back-nav';
import { clearEstablishmentsCache, type EstablishmentRow } from '@/lib/establishments';
import { formatBps, formatDate, formatDateTime, formatKop } from '@/lib/format';
import { callTarget, telHref } from '@/lib/phone';
import { ROLE_FULL, ROLE_LABEL } from '@/lib/sales';
import { daysLeftLabel } from '@/lib/subscription';
import { useAuthStore, type ManagerRole } from '@/store/auth.store';

interface MemberCard {
  manager: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
    code: string;
    role: ManagerRole;
    status: 'active' | 'blocked';
    commissionBps: number;
    avatarUrl?: string | null;
    lastLoginAt: string | null;
    createdAt: string;
    createdBy: { id: string; name: string } | null;
    referralLink: string;
  };
  stats: MemberStatsData;
  establishments: EstablishmentRow[];
  recentPayments: AccrualItem[];
  byMonth: MonthItem[];
  payouts: PayoutItem[];
  canDelete: boolean;
  /** Профиль и ставку правит руководитель. Нет у старого сервера. */
  canEdit?: boolean;
}

export default function ManagerCard() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const back = readBackState(location.state);
  const [data, setData] = useState<MemberCard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  // После правки карточку перечитываем, не теряя прокрутку.
  const [version, setVersion] = useState(0);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setError(null);
    void apiGet<MemberCard>(`/managers/${id}`, { silent: true })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err: AxiosError) => {
        if (cancelled) return;
        setData(null);
        setError(apiErrorMessage(err, 'Не удалось открыть карточку'));
      });
    return () => {
      cancelled = true;
    };
  }, [id, version]);

  const backHref = back?.backTo ?? '/managers';
  const backLabel = back?.backLabel ?? 'Команда';
  const goBack = (e?: React.MouseEvent) => {
    if (!back) return;
    e?.preventDefault();
    navigate(-back.backSteps);
  };

  if (error) {
    return (
      <div className="max-w-4xl space-y-3">
        <div className="card p-4 text-sm text-ink/70">{error}</div>
        <Link to={backHref} onClick={goBack} className="btn-ghost !px-0">
          <ArrowLeft size={16} /> {backLabel}
        </Link>
      </div>
    );
  }
  if (!data || data.manager.id !== id) return <div className="text-ink/50 text-sm">Загрузка…</div>;

  const m = data.manager;
  const cardBack: BackState = { backTo: `/managers/${m.id}`, backSteps: 1, backLabel: m.name };

  const onDeleted = (res: DeleteResult) => {
    setDeleting(false);
    toast.success(deletedMessage(m.name, res));
    if (back) navigate(-back.backSteps);
    else navigate('/managers', { replace: true });
  };

  return (
    <div className="max-w-4xl space-y-5">
      <Link
        to={backHref}
        onClick={goBack}
        className="inline-flex items-center gap-1.5 text-sm text-ink/55 hover:text-ink"
      >
        <ArrowLeft size={16} /> {backLabel}
      </Link>

      <MemberHeader member={data} onEdit={data.canEdit ? () => setEditing(true) : undefined} />
      <MemberStatsGrid stats={data.stats} />
      <MemberEstablishments rows={data.establishments} backState={cardBack} />
      <AccrualsList
        items={data.recentPayments}
        emptyText="Его заведения ещё не оплачивали подписку."
        backState={cardBack}
      />
      <MonthsTable rows={data.byMonth} />
      <PayoutsList items={data.payouts} />

      {data.canDelete ? (
        <section className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
          <div className="text-sm">
            <div className="font-medium">Удалить из команды</div>
            <p className="mt-0.5 text-ink/55">
              Вход закроется сразу. Начисления и выплаты останутся в истории, заведения можно передать.
            </p>
          </div>
          <button
            type="button"
            className="btn shrink-0 border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
            onClick={() => setDeleting(true)}
          >
            <Trash2 size={16} /> Удалить
          </button>
        </section>
      ) : null}

      {editing ? (
        <EditMemberSheet
          member={m}
          establishments={data.stats.establishments}
          onClose={() => setEditing(false)}
          onSaved={(res) => {
            setEditing(false);
            // Ставка в строках списка заведений — снимок, он поменялся.
            if (res.changed.includes('commissionBps')) clearEstablishmentsCache();
            toast.success(savedMessage(res));
            setVersion((v) => v + 1);
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteMemberSheet
          member={m}
          establishments={data.stats.establishments}
          pendingKopecks={data.stats.pendingKopecks}
          onClose={() => setDeleting(false)}
          onDeleted={onDeleted}
        />
      ) : null}
    </div>
  );
}

function MemberHeader({ member, onEdit }: { member: MemberCard; onEdit?: () => void }) {
  const m = member.manager;
  const phone = telHref(m.phone);
  return (
    <section className="card overflow-hidden">
      <div className="flex flex-col gap-4 p-4 sm:flex-row sm:items-start sm:justify-between sm:p-5">
        <div className="flex min-w-0 items-center gap-3.5">
          <Avatar name={m.name} url={m.avatarUrl} size={56} />
          <div className="min-w-0">
            <h1 className="truncate text-lg sm:text-xl font-semibold leading-tight">{m.name}</h1>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <span className="inline-flex rounded-md bg-orange/10 px-2 py-0.5 text-[11px] font-medium text-orange-700">
                {ROLE_FULL[m.role]}
              </span>
              {m.status === 'blocked' ? (
                <span className="inline-flex rounded-md bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700">
                  заблокирован
                </span>
              ) : null}
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-line bg-paper px-4 py-3 sm:min-w-[180px] sm:text-right">
          <div className="text-xs text-ink/55">Ставка</div>
          <div className="mt-0.5 text-2xl font-semibold tabular-nums">{formatBps(m.commissionBps)}</div>
          <div className="mt-0.5 text-[11px] text-ink/50">
            {onEdit ? 'задаёт руководитель отдела' : 'задаёт администратор Resstro'}
          </div>
        </div>
      </div>

      {phone || m.email || onEdit ? (
        <div className="flex flex-wrap gap-2 border-t border-line/60 px-4 py-3 sm:px-5">
          {onEdit ? (
            <button type="button" className="btn border border-line bg-white text-ink hover:bg-paper" onClick={onEdit}>
              <Pencil size={16} /> Изменить
            </button>
          ) : null}
          {phone && m.phone ? (
            <a
              href={phone}
              className="btn border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            >
              <Phone size={16} /> {m.phone}
            </a>
          ) : null}
          <a href={`mailto:${m.email}`} className="btn max-w-full border border-line bg-white text-ink/75 hover:bg-paper">
            <Mail size={16} className="shrink-0" /> <span className="truncate">{m.email}</span>
          </a>
        </div>
      ) : null}

      <dl className="grid border-t border-line/60 text-sm sm:grid-cols-2">
        <Row label="Код для ссылки">
          <span className="font-mono">{m.code}</span>
        </Row>
        <Row label="В команде с">{formatDate(m.createdAt)}</Row>
        <Row label="Добавил">{m.createdBy ? m.createdBy.name : 'администратор Resstro'}</Row>
        <Row label="Последний вход">{m.lastLoginAt ? formatDateTime(m.lastLoginAt) : 'ещё не входил'}</Row>
      </dl>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-line/60 px-4 py-3 last:border-b-0 sm:px-5 sm:odd:border-r sm:[&:nth-last-child(2)]:border-b-0">
      <dt className="text-ink/55">{label}</dt>
      <dd className="min-w-0 truncate text-right font-medium">{children}</dd>
    </div>
  );
}

/** Заведения сотрудника — самые оборотистые сверху; звонок рядом, не внутри ссылки. */
function MemberEstablishments({ rows, backState }: { rows: EstablishmentRow[]; backState: BackState }) {
  return (
    <section className="card overflow-hidden">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-line px-4 py-3">
        <h2 className="text-sm font-medium">Заведения · {rows.length}</h2>
        <span className="text-xs text-ink/45">по обороту кассы за 30 дней</span>
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-6 text-sm text-ink/50">Заведений пока нет.</div>
      ) : (
        <ul className="divide-y divide-line/60">
          {rows.map((r) => {
            const call = callTarget(r);
            return (
              <li key={r.id} className="flex items-center gap-2 pr-2">
                <Link
                  to={`/establishments/${r.id}`}
                  state={backState}
                  className="flex min-w-0 flex-1 items-center justify-between gap-3 px-4 py-3 active:bg-paper hover:bg-paper/60"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium">{r.name}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink/50">
                      <StatusPill sub={r.subscription} />
                      {r.subscription ? <span>{daysLeftLabel(r.subscription)}</span> : null}
                      {r.city ? <span className="truncate">{r.city}</span> : null}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className={clsx('font-semibold tabular-nums', !r.revenue30dKopecks && 'text-ink/35')}>
                      {formatKop(r.revenue30dKopecks)}
                    </div>
                    <div className="text-[11px] text-ink/45">комиссия {formatKop(r.commissionTotalKopecks)}</div>
                  </div>
                </Link>
                {call ? (
                  <a
                    href={call.href}
                    title={`Позвонить: ${call.who} · ${call.phone}`}
                    aria-label={`Позвонить: ${call.who}`}
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-emerald-600 hover:bg-emerald-50 active:bg-emerald-100"
                  >
                    <Phone size={17} />
                  </a>
                ) : (
                  <span className="h-10 w-10 shrink-0" aria-hidden />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

interface UpdateResult {
  ok: boolean;
  changed: string[];
  /** Сколько заведений получили новую ставку. */
  establishments: number;
}

function savedMessage(res: UpdateResult): string {
  if (!res.changed.length) return 'Изменений нет';
  if (res.changed.includes('commissionBps')) {
    return res.establishments
      ? `Сохранено · новая ставка у его заведений: ${res.establishments}`
      : 'Сохранено · новая ставка для будущих заведений';
  }
  return 'Профиль сохранён';
}

/** «12,5» → 1250 б.п.; null — не число или вне 0–100 %. */
function parsePercentToBps(raw: string): number | null {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.').replace('%', '');
  // Пустое поле — не «0 %»: стёртая ставка не должна обнулить комиссию.
  if (!cleaned) return null;
  const n = Number(cleaned);
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return Math.round(n * 100);
}

function EditMemberSheet({
  member,
  establishments,
  onClose,
  onSaved,
}: {
  member: MemberCard['manager'];
  establishments: number;
  onClose: () => void;
  onSaved: (res: UpdateResult) => void;
}) {
  const [name, setName] = useState(member.name);
  const [phone, setPhone] = useState(member.phone ?? '');
  const [email, setEmail] = useState(member.email);
  const [percent, setPercent] = useState(String(member.commissionBps / 100).replace('.', ','));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bps = parsePercentToBps(percent);
  const rateChanged = bps != null && bps !== member.commissionBps;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (name.trim().length < 2) return setError('Имя — от 2 символов');
    if (bps == null) return setError('Ставка — число от 0 до 100 %');
    // Только изменённое: сервер пишет в журнал «было → стало».
    const body: Record<string, unknown> = {};
    if (name.trim() !== member.name) body.name = name.trim();
    if (phone.trim() !== (member.phone ?? '')) body.phone = phone.trim() || null;
    if (email.trim().toLowerCase() !== member.email) body.email = email.trim();
    if (rateChanged) body.commissionBps = bps;
    if (!Object.keys(body).length) return onClose();
    setBusy(true);
    setError(null);
    try {
      onSaved(await apiPatch<UpdateResult>(`/managers/${member.id}`, body, { silent: true }));
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось сохранить'));
      setBusy(false);
    }
  };

  return (
    <Sheet title="Профиль сотрудника" onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
        <div>
          <label className="label" htmlFor="member-name">Имя</label>
          <input id="member-name" className="input" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="member-phone">Телефон</label>
          <input
            id="member-phone"
            className="input"
            type="tel"
            inputMode="tel"
            maxLength={32}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="member-email">Email — вход в кабинет</label>
          <input
            id="member-email"
            className="input"
            type="email"
            inputMode="email"
            autoComplete="off"
            maxLength={200}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {email.trim().toLowerCase() !== member.email ? (
            <p className="mt-1 text-xs text-amber-700">Код для входа будет приходить на новый адрес.</p>
          ) : null}
        </div>
        <div>
          <label className="label" htmlFor="member-rate">Ставка, %</label>
          <input
            id="member-rate"
            className="input"
            inputMode="decimal"
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
          />
          <p className="mt-1 text-xs text-ink/50">
            {rateChanged
              ? `С будущих оплат: ${formatBps(member.commissionBps)} → ${formatBps(bps!)}${
                  establishments ? ` у всех его заведений (${establishments})` : ''
                }. Уже начисленное не пересчитывается.`
              : 'Процент с каждой оплаты подписки его заведений.'}
          </p>
        </div>
        {error ? <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          {busy ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </form>
    </Sheet>
  );
}

interface DeleteResult {
  ok: boolean;
  establishments: number;
  transferredTo: { id: string; name: string } | null;
  pendingKopecks: number;
}

function deletedMessage(name: string, res: DeleteResult): string {
  if (!res.establishments) return `${name} удалён из команды`;
  const what = establishmentsCount(res.establishments);
  return res.transferredTo
    ? `${name} удалён · ${what} теперь у ${res.transferredTo.name}`
    : `${name} удалён · ${what} без менеджера`;
}

interface TeamOption {
  id: string;
  name: string;
  role: ManagerRole;
  status: 'active' | 'blocked';
  establishments: number;
}

const NOBODY = '__nobody__';

function DeleteMemberSheet({
  member,
  establishments,
  pendingKopecks,
  onClose,
  onDeleted,
}: {
  member: MemberCard['manager'];
  establishments: number;
  pendingKopecks: number;
  onClose: () => void;
  onDeleted: (res: DeleteResult) => void;
}) {
  const myId = useAuthStore((s) => s.manager?.id);
  const [team, setTeam] = useState<TeamOption[] | null>(null);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!establishments) return;
    let cancelled = false;
    void apiGet<TeamOption[]>('/managers', { silent: true })
      .then((rows) => {
        if (!cancelled) setTeam(rows);
      })
      .catch(() => {
        if (!cancelled) setTeam([]);
      });
    return () => {
      cancelled = true;
    };
  }, [establishments]);

  // Передать можно активному сотруднику, кроме удаляемого; себя — первым.
  const options = useMemo(
    () =>
      (team ?? [])
        .filter((t) => t.id !== member.id && t.status === 'active')
        .sort((a, b) => (a.id === myId ? -1 : b.id === myId ? 1 : a.name.localeCompare(b.name, 'ru'))),
    [team, member.id, myId],
  );

  const needsChoice = establishments > 0;
  const ready = !needsChoice || choice !== '';

  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiDelete<DeleteResult>(`/managers/${member.id}`, {
        silent: true,
        body: { transferTo: choice && choice !== NOBODY ? choice : null },
      });
      // Привязки заведений поменялись — список в памяти устарел.
      clearEstablishmentsCache();
      onDeleted(res);
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось удалить сотрудника'));
      setBusy(false);
    }
  };

  return (
    <Sheet title="Удалить из команды" onClose={onClose}>
      <div className="space-y-4 text-sm">
        <div className="rounded-lg border border-line px-3 py-2.5">
          <div className="font-medium">{member.name}</div>
          <div className="text-xs text-ink/50">
            {ROLE_FULL[member.role]} · {member.email}
          </div>
        </div>
        <ul className="space-y-1.5 text-ink/65">
          <li>Вход в кабинет закроется сразу, открытые вкладки отключатся.</li>
          <li>Начисления и выплаты останутся в истории — по ним считается сейф.</li>
          <li>Вернуть сотрудника может администратор Resstro.</li>
        </ul>

        {pendingKopecks > 0 ? (
          <div className="rounded-lg bg-amber-50 px-3 py-2.5 text-amber-800">
            К выплате у него {formatKop(pendingKopecks)} — выплату проводит администратор Resstro.
          </div>
        ) : null}

        {needsChoice ? (
          <div>
            <label className="label" htmlFor="transfer-to">
              Его {establishmentsCount(establishments)} — кому передать?
            </label>
            <select
              id="transfer-to"
              className="input"
              value={choice}
              onChange={(e) => setChoice(e.target.value)}
              disabled={team === null}
            >
              <option value="" disabled>
                {team === null ? 'Загружаем команду…' : 'Выберите'}
              </option>
              {options.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.id === myId
                    ? `Себе (${t.name})`
                    : `${t.name} · ${ROLE_LABEL[t.role]} · заведений: ${t.establishments}`}
                </option>
              ))}
              <option value={NOBODY}>Оставить без менеджера</option>
            </select>
            <p className="mt-1.5 text-xs text-ink/50">
              {choice && choice !== NOBODY
                ? 'Комиссия с будущих оплат пойдёт новому менеджеру по его ставке.'
                : choice === NOBODY
                  ? 'Комиссию с этих заведений не получит никто, пока их не привяжут.'
                  : 'Прошлые начисления остаются у удаляемого.'}
            </p>
          </div>
        ) : null}

        {error ? <div className="rounded-lg bg-rose-50 px-3 py-2 text-rose-700">{error}</div> : null}

        <div className="grid gap-2 sm:grid-cols-2">
          <button type="button" className="btn border border-line bg-white text-ink hover:bg-paper" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
            disabled={!ready || busy}
            onClick={() => void submit()}
          >
            <UserX size={16} /> {busy ? 'Удаляем…' : 'Удалить'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
